import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, X } from 'lucide-react';
import { SearchInput } from '@/components/ui/search-input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface PageOption {
  id: string;
  name: string;
}

interface Props {
  pages: PageOption[];
  /** Selected page ids; empty means "All Page Sections". */
  value: string[];
  onChange: (ids: string[]) => void;
}

/**
 * Multi-select filter for DM page sections (Schedule Calendar).
 * Inline panel, like DmUserMultiPicker, so it needs no portal.
 */
export function DmPageMultiSelect({ pages, value, onChange }: Props) {
  const [open, setOpenState] = useState(false);
  const [search, setSearch] = useState('');
  function setOpen(next: boolean) {
    setOpenState(next);
    if (!next) setSearch('');
  }
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? pages.filter(p => p.name.toLowerCase().includes(q)) : pages;
  }, [pages, search]);

  function toggle(id: string) {
    onChange(value.includes(id) ? value.filter(x => x !== id) : [...value, id]);
  }

  const triggerText = (() => {
    if (value.length === 0) return 'All Page Sections';
    if (value.length === 1) return pages.find(p => p.id === value[0])?.name ?? '1 page section';
    return `${value.length} page sections`;
  })();

  const allSelected = value.length === 0;

  return (
    <div ref={rootRef} className="relative">
      <Button
        type="button"
        variant="outline"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen(!open)}
        className={cn(
          // Same 36px control look as the calendar toolbar's month switcher and Today button.
          'h-9 w-56 justify-between gap-2 rounded-md border-slate-200 bg-white/80 px-3 font-normal text-sm shadow-sm sm:h-9 sm:px-3 dark:border-border dark:bg-card',
          !allSelected && 'border-teal-300 bg-teal-50/60 text-teal-800 hover:bg-teal-50 dark:border-teal-800 dark:bg-teal-900/20 dark:text-teal-300',
        )}
      >
        <span className="truncate">{triggerText}</span>
        <span className="flex shrink-0 items-center gap-1.5">
          {!allSelected && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-teal-600 px-1.5 text-[10px] font-semibold text-white">
              {value.length}
            </span>
          )}
          <ChevronDown className={cn('h-4 w-4 opacity-50 transition-transform', open && 'rotate-180')} />
        </span>
      </Button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          className="absolute left-0 top-full z-50 mt-1 w-[min(280px,calc(100vw-2rem))] overflow-hidden rounded-xl border border-border/60 bg-card shadow-lg animate-in fade-in-0 zoom-in-95 duration-150 origin-top"
        >
          <div className="border-b border-border/60 p-2">
            <SearchInput
              ref={inputRef}
              value={search}
              onChange={setSearch}
              placeholder="Search page sections…"
              containerClassName="w-full"
            />
          </div>

          <div className="thin-scrollbar max-h-72 overflow-y-auto overscroll-contain p-1">
            {!search && (
              <button
                type="button"
                role="option"
                aria-selected={allSelected}
                onClick={() => onChange([])}
                className={cn(
                  'flex min-h-[36px] w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                  allSelected ? 'bg-primary/10 font-medium text-primary hover:bg-primary/15' : 'hover:bg-muted/50',
                )}
              >
                <CheckBox checked={allSelected} />
                All Page Sections
              </button>
            )}
            {filtered.length === 0 && (
              <p className="p-4 text-center text-sm text-muted-foreground">No page sections found</p>
            )}
            {filtered.map(p => {
              const isSel = value.includes(p.id);
              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={isSel}
                  key={p.id}
                  onClick={() => toggle(p.id)}
                  className={cn(
                    'flex min-h-[36px] w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                    isSel ? 'bg-primary/10 hover:bg-primary/15' : 'hover:bg-muted/50',
                  )}
                >
                  <CheckBox checked={isSel} />
                  <span className="min-w-0 flex-1 truncate">{p.name}</span>
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-2 border-t border-border/60 px-3 py-2">
            <span className="text-xs text-muted-foreground">
              {allSelected ? 'Showing all' : `${value.length} selected`}
            </span>
            <span className="flex items-center gap-3">
              {!allSelected && (
                <button
                  type="button"
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => onChange([])}
                >
                  <X className="h-3 w-3" /> Clear
                </button>
              )}
              <button
                type="button"
                className="text-xs font-medium text-primary hover:underline"
                onClick={() => setOpen(false)}
              >
                Done
              </button>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function CheckBox({ checked }: { checked: boolean }) {
  return (
    <span className={cn(
      'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
      checked ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
    )}>
      {checked && <Check className="h-3 w-3" />}
    </span>
  );
}
